import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from './mergeModalContent.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import api from '../../../../api/api';
import InputField from '../../../_common/Form/TextFields/InputField';
import DOBPicker from '../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import Select from '../../../_common/Form/Selects/Select/Select';
import { ReactComponent as ArrowIcon } from '../../../../assets/icons/arrow-right.svg';
import utils from '../../../../utils/utils';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';
import accountHelpers from '../../../../utils/accountHelpers';
import { insuranceTemplate, stateOptions } from '../../../../utils/data/data';

let timeout;

const patientInfoTemplate = [
  {
    path: 'firstName',
    label: 'First Name',
    type: 'input',
    validation: 'required',
  },
  {
    path: 'lastName',
    label: 'Last Name',
    type: 'input',
    validation: 'required',
  },
  {
    path: 'dob',
    label: 'Date of Birth',
    type: 'calendar',
    validation: 'date',
  },
  {
    path: 'hub',
    label: 'Hub',
    type: 'input',
  },
  {
    path: 'sex',
    label: 'Sex',
    type: 'select',
    options: ['Male', 'Female', 'Other'],
  },
  {
    path: 'phone',
    label: 'Phone',
    type: 'input',
    validation: 'phone',
  },
  {
    path: 'phone2',
    label: 'Phone 2',
    type: 'input',
    validation: 'phone',
  },
  {
    path: 'phone3',
    label: 'Phone 3',
    type: 'input',
    validation: 'phone',
  },
  {
    path: 'email',
    label: 'Email',
    type: 'input',
    validation: 'email',
  },
  {
    path: 'patientWarning',
    label: 'Patient Warning',
    type: 'input',
  },
  {
    path: 'conditions',
    label: 'Conditions',
    type: 'input',
  },
  {
    path: 'allergies',
    label: 'Allergies',
    type: 'input',
  },
  {
    path: 'addressStreet',
    label: 'Address - Street',
    type: 'input',
  },
  {
    path: 'addressCity',
    label: 'Address - City',
    type: 'input',
  },
  {
    path: 'addressState',
    label: 'Address - State',
    type: 'select',
    options: stateOptions,
  },
  {
    path: 'addressZipCode',
    label: 'Address - ZipCode',
    type: 'input',
    validation: 'number',
  },
  {
    path: 'address2Street',
    label: 'Address 2 - Street',
    type: 'input',
  },
  {
    path: 'address2City',
    label: 'Address 2 - City',
    type: 'input',
  },
  {
    path: 'address2State',
    label: 'Address 2 - State',
    type: 'select',
    options: stateOptions,
  },
  {
    path: 'address2ZipCode',
    label: 'Address 2 - ZipCode',
    type: 'input',
    validation: 'number',
  },
  {
    path: 'address3Street',
    label: 'Address 3 - Street',
    type: 'input',
  },
  {
    path: 'address3City',
    label: 'Address 3 - City',
    type: 'input',
  },
  {
    path: 'address3State',
    label: 'Address 3 - State',
    type: 'select',
    options: stateOptions,
  },
  {
    path: 'address3ZipCode',
    label: 'Address 3 - ZipCode',
    type: 'input',
    validation: 'number',
  },
];

const MergeModalContent = ({ onCancel }) => {
  const [deletingPatientList, setDeletingPatientList] = useState({
    deletingPatients: [],
    updatingPatients: [],
  });

  const [searchPatientValue, setSearchPatientValue] = useState({
    deletingValue: '',
    updatingValue: '',
  });

  const [selectedPatient, setSelectedPatient] = useState({
    deletingPatient: {},
    updatingPatient: {},
  });

  const [updatingPatientDefaultData, setUpdatingPatientDefaultData] = useState({});

  const { deletingPatients, updatingPatients } = deletingPatientList;

  const { deletingValue, updatingValue } = searchPatientValue;

  const { deletingPatient, updatingPatient } = selectedPatient;

  const [searchPatientLoading, setSearchPatientLoading] = useState('');

  const [errors, setErrors] = useState({});

  const [isAttentionModalOpen, attentionModalToggle] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const searchPatient = (path, value) => {
    setSearchPatientValue((prev) => ({ ...prev, [`${path}Value`]: value }));

    clearTimeout(timeout);
    setDeletingPatientList((prev) => ({ ...prev, [`${path}Patients`]: [] }));

    if (value.trim()) {
      setSearchPatientLoading(path);

      timeout = setTimeout(async () => {
        const { data: { data: { response } } } = await api.getPatients({ limit: 100, name: value });
        const patients = response.map((r) => ({ ...r, name: `${r.firstName} ${r.lastName}` }))
          .filter(({ id }) => id !== (path === 'deleting' ? updatingPatient?.id : deletingPatient?.id));

        setDeletingPatientList((prev) => ({ ...prev, [`${path}Patients`]: patients }));

        setSearchPatientLoading('');
      }, 400);
    } else {
      setSearchPatientLoading('');
    }
  };

  const setPatient = (path, option, searchValue) => {
    const patient = { ...option };

    if (option.Prim) {
      insuranceTemplate.forEach((i) => {
        patient[`prim${i.path}`] = option.Prim[i.backPath];
      });
    }

    if (option.Sec) {
      insuranceTemplate.forEach((i) => {
        patient[`sec${i.path}`] = option.Sec[i.backPath];
      });
    }

    delete patient.updatedAt;
    delete patient.createdAt;
    delete patient.Prim;
    delete patient.Sec;

    setSearchPatientValue((prev) => ({ ...prev, [`${path}Value`]: searchValue }));

    setSelectedPatient((prev) => ({ ...prev, [`${path}Patient`]: patient }));

    if (path === 'updating') {
      setUpdatingPatientDefaultData(patient);
      setErrors({});
    }
  };

  const changePatient = (path, value) => {
    if (path === 'all') {
      setErrors({});

      const updatingFields = {};

      patientInfoTemplate.forEach((d) => {
        updatingFields[d.path] = deletingPatient[d.path];
      });

      insuranceTemplate.forEach((d) => {
        updatingFields[`prim${d.path}`] = deletingPatient[`prim${d.path}`];
        updatingFields[`sec${d.path}`] = deletingPatient[`sec${d.path}`];
      });

      setSelectedPatient((prev) => ({
        ...prev,
        updatingPatient: { ...prev.updatingPatient, ...updatingFields },
      }));
    } else {
      setErrors((prev) => ({ ...prev, [path]: '' }));

      setSelectedPatient((prev) => ({
        ...prev,
        updatingPatient: {
          ...prev.updatingPatient,
          [path]: value,
        },
      }));
    }
  };

  const onKeyPress = (e, validationType, isPaste) => {
    if (validationType === 'number') {
      utils.keyPressOnlyNumber(e, isPaste);
    } else if (validationType === 'phone') {
      utils.keyPressPhoneNumber(e, isPaste);
    } else {
      () => {};
    }
  };

  const margePatient = () => {
    let hasError = false;

    patientInfoTemplate.forEach(({ path, validation }) => {
      if (validation === 'required' && !updatingPatient[path]) {
        setErrors((prev) => ({ ...prev, [path]: 'Field is required' }));
        hasError = true;
      } else if (validation === 'email' && updatingPatient[path] && !utils.isEmail(updatingPatient[path])) {
        setErrors((prev) => ({ ...prev, [path]: 'Invalid email' }));
        hasError = true;
      } else if (validation === 'date' && updatingPatient[path] && updatingPatient[path].length !== 10) {
        setErrors((prev) => ({ ...prev, [path]: 'Invalid date' }));
        hasError = true;
      }
    });

    if (!hasError) {
      attentionModalToggle(true);
    } else {
      toast.error('Please correct all errors');
    }
  };

  const onSave = async () => {
    setSaveLoading(true);
    await api.mergeScripts({ oldPatientId: deletingPatient.id, patientId: updatingPatient.id });

    api.deletePatient({ patientId: deletingPatient.id, userId: accountHelpers.getUser().id });

    const data = {
      ...updatingPatient,
      userId: accountHelpers.getUser().id,
    };

    delete data.name;

    try {
      await api.updatePatient(data);

      window.location.reload();
    } catch (e) {
      console.warn(e);
    }

    setSaveLoading(false);
  };

  return (
    <div className={classes.wrapper}>
      <h2>Merge Patient</h2>

      <div className={classes.btns_wrapper}>
        <Button
          title="Cancel"
          type="button"
          clearButton
          onClick={onCancel}
        />

        <Button
          title="Merge"
          type="button"
          reverseColor
          onClick={margePatient}
          disabled={!deletingPatient.id || !updatingPatient.id}
        />
      </div>

      <div className={classes.content}>
        <div className={classes.fields_wrapper}>
          <div className={classes.field}>
            <SelectWithSearch
              label="Type name or patient"
              searchValue={deletingValue}
              options={deletingPatients}
              optionKey=""
              loading={searchPatientLoading === 'deleting'}
              onChange={(value) => searchPatient('deleting', value)}
              onSelectOption={(option, searchValue) => setPatient('deleting', option || {}, searchValue)}
              zIndex={30}
            />

            {deletingPatient.id && <div className={classes.attention_text}>This patient will be deleted!</div>}
          </div>

          <div className={classes.field}>
            <SelectWithSearch
              label="Type name or patient"
              searchValue={updatingValue}
              options={updatingPatients}
              optionKey=""
              loading={searchPatientLoading === 'updating'}
              onChange={(value) => searchPatient('updating', value)}
              onSelectOption={(option, searchValue) => setPatient('updating', option || {}, searchValue)}
              zIndex={30}
            />

            {updatingPatient.id && <div className={classes.attention_text}>This patient will be updated!</div>}
          </div>
        </div>

        <h3>Patient Info</h3>

        <div className={classes.select_all}>
          <Button
            className={classes.marge_btn}
            title="Merge All"
            buttonColor="#0087b0"
            reverseColor
            onClick={() => changePatient('all')}
            disabled={!deletingPatient.id || !updatingPatient.id}
          />
        </div>

        <div className={classes.user_info}>
          {patientInfoTemplate.map(({
            path, label, type, validation, options,
          }) => (
            <div key={path} className={classes.info_field_rapper}>
              <InputField
                onChange={() => {}}
                placeholder={(type === 'calendar' && deletingPatient[path]
                  ? moment(deletingPatient[path]).format('MM/DD/YYYY') : deletingPatient[path]) || '-'}
                disabled
                label={label}
              />

              <Button
                className={classes.marge_btn}
                title={(
                  <ArrowIcon />
                )}
                buttonColor="#0087b0"
                reverseColor
                onClick={() => {
                  if (!_.isEmpty(deletingPatient)) {
                    changePatient(path, deletingPatient[path]);
                  }
                }}
              />

              {type === 'calendar'
                ? (
                  <DOBPicker
                    label={label}
                    date={updatingPatient[path] || ''}
                    onChange={(date) => changePatient(path, date)}
                    placeholder={updatingPatientDefaultData[path]
                      ? moment(updatingPatientDefaultData[path]).format('MM.DD.YYYY')
                      : '-'}
                    dateFormat="MM.DD.YYYY"
                    closeOnScroll
                    autoValidation={false}
                    error={errors[path]}
                  />
                )
                : type === 'select'
                  ? (
                    <Select
                      label={label}
                      onChange={(option) => changePatient(path, option)}
                      currentOption={updatingPatient[path] || ''}
                      options={options}
                      placeholder={updatingPatientDefaultData[path] || '-'}
                      zIndex={30}
                      closeOnScroll
                    />
                  )
                  : (
                    <InputField
                      label={label}
                      value={updatingPatient[path] || ''}
                      placeholder={updatingPatientDefaultData[path] || '-'}
                      onChange={(value) => changePatient(path, value)}
                      error={errors[path]}
                      errorClassName={classes.error_text}
                      onKeyPress={(e) => onKeyPress(e, validation)}
                      onPaste={(e) => onKeyPress(e, validation, true)}
                    />
                  )}
            </div>
          ))}

          <h4>Primary Insurance</h4>

          {insuranceTemplate.map(({
            path, label, type, options,
          }) => (
            <div key={path} className={classes.info_field_rapper}>
              <InputField
                onChange={() => {}}
                placeholder={deletingPatient[`prim${path}`] || '-'}
                disabled
                label={label}
              />

              <Button
                className={classes.marge_btn}
                title={(
                  <ArrowIcon />
                )}
                buttonColor="#0087b0"
                reverseColor
                onClick={() => {
                  if (!_.isEmpty(deletingPatient)) {
                    changePatient(`prim${path}`, deletingPatient[`prim${path}`]);
                  }
                }}
              />

              {type === 'select'
                ? (
                  <Select
                    label={label}
                    onChange={(option) => changePatient(`prim${path}`, option)}
                    currentOption={updatingPatient[`prim${path}`] || ''}
                    options={options}
                    placeholder={updatingPatientDefaultData[`prim${path}`] || '-'}
                    zIndex={30}
                    closeOnScroll
                  />
                )
                : (
                  <InputField
                    value={updatingPatient[`prim${path}`] || ''}
                    placeholder={updatingPatientDefaultData[`prim${path}`] || '-'}
                    onChange={(value) => changePatient(`prim${path}`, value)}
                    label={label}
                  />
                )}
            </div>
          ))}

          <h4>Secondary Insurance</h4>

          {insuranceTemplate.map(({
            path, label, type, options,
          }) => (
            <div key={path} className={classes.info_field_rapper}>
              <InputField
                onChange={() => {}}
                placeholder={deletingPatient[`sec${path}`] || '-'}
                disabled
                label={label}
              />

              <Button
                className={classes.marge_btn}
                title={(
                  <ArrowIcon />
                )}
                buttonColor="#0087b0"
                reverseColor
                onClick={() => {
                  if (!_.isEmpty(deletingPatient)) {
                    changePatient(`sec${path}`, deletingPatient[`sec${path}`]);
                  }
                }}
              />

              {type === 'select'
                ? (
                  <Select
                    label={label}
                    onChange={(option) => changePatient(`sec${path}`, option)}
                    currentOption={updatingPatient[`sec${path}`] || ''}
                    options={options}
                    placeholder={updatingPatientDefaultData[`sec${path}`] || '-'}
                    zIndex={30}
                    closeOnScroll
                  />
                )
                : (
                  <InputField
                    value={updatingPatient[`sec${path}`] || ''}
                    placeholder={updatingPatientDefaultData[`sec${path}`] || '-'}
                    onChange={(value) => changePatient(`sec${path}`, value)}
                    label={label}
                  />
                )}
            </div>
          ))}
        </div>
      </div>

      <AttentionModal
        modalClassName={classes.attention_modal}
        isOpen={isAttentionModalOpen}
        onClose={() => attentionModalToggle(false)}
        title={`This will delete ${deletingPatient.name} and update ${updatingPatient.name}`}
        text={`The scripts associated with ${deletingPatient.name} will be reassigned to ${updatingPatient.name}`}
        zIndex={31}
        onSave={onSave}
        loading={saveLoading}
      />
    </div>
  );
};

MergeModalContent.propTypes = {
  onCancel: PropTypes.func.isRequired,
};

export default MergeModalContent;
